import React, { useState, useEffect } from "react";
import { format, addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ro } from "date-fns/locale";
import { PlanetaryHour } from "../utils/planetaryHours";
import CalendarSaveButton from "./CalendarSaveButton";

interface PlanetaryHoursListProps {
  hours: PlanetaryHour[];
  selectedTimezone?: string;
}

const PlanetaryHoursList: React.FC<PlanetaryHoursListProps> = ({
  hours,
  selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);
  const formatTime = (date: Date) => {
    return formatInTimeZone(date, selectedTimezone, "HH:mm");
  };

  // Get the current date from the first hour's start time
  const currentDate = hours.length > 0 ? hours[0].startTime : new Date();
  // Get the next date for night hours that extend to the next day
  const nextDate = addDays(currentDate, 1);

  // Format the dates for display (e.g., "23 - 24 aprilie")
  const formatDateDay = (date: Date) => format(date, "d", { locale: ro });
  const formatDateMonth = (date: Date) => format(date, "MMMM", { locale: ro });

  // Create the date range string
  const dateRangeStr = `${formatDateDay(currentDate)} - ${formatDateDay(nextDate)} ${formatDateMonth(currentDate)}`;

  // Check if an hour is current
  const isCurrentHour = (hour: PlanetaryHour): boolean => {
    const now = currentTime.getTime();
    return now >= hour.startTime.getTime() && now < hour.endTime.getTime();
  };

  return (
    <div className="planetary-hours-list">
      <h3>
        Orele Planetare ale zilei ({dateRangeStr})<br />
        (Hora Muhurta)
      </h3>
      <div className="hours-container">
        <div className="day-hours">
          <h4>Ziuă (Răsărit - Apus) </h4>
          <ul>
            {hours.slice(0, 12).map((hour, index) => (
              <li
                key={index}
                className={`hour-item ${isCurrentHour(hour) ? "current-interval" : ""}`}
              >
                <span className="hour-time">
                  {" "}
                  {formatTime(hour.startTime)} - {formatTime(hour.endTime)}{" "}
                </span>
                <span className="hour-planet">
                  <span className="planet-symbol"> {hour.symbol} </span>{" "}
                  {hour.planet}
                </span>
                <div className="muhurta-actions" style={{ marginLeft: "10px" }}>
                  <CalendarSaveButton
                    event={{
                      title: `Ora Planetara: ${hour.planet}`,
                      description: `Ora guvernata de planeta: ${hour.planet}\nSimbol: ${hour.symbol}`,
                      startTime: hour.startTime,
                      endTime: hour.endTime,
                    }}
                    showIcon={false}
                  >
                    <img
                      src="/images/apple-calendar-icon.png"
                      alt="Calendar"
                      width="16"
                      height="16"
                    />
                  </CalendarSaveButton>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="night-hours">
          <h4>Noapte (Apus - Răsărit) </h4>
          <ul>
            {hours.slice(12, 24).map((hour, index) => (
              <li
                key={index + 12}
                className={`hour-item ${isCurrentHour(hour) ? "current-interval" : ""}`}
              >
                <span className="hour-time">
                  {" "}
                  {formatTime(hour.startTime)} - {formatTime(hour.endTime)}{" "}
                </span>
                <span className="hour-planet">
                  <span className="planet-symbol"> {hour.symbol} </span>{" "}
                  {hour.planet}
                </span>
                <div className="muhurta-actions" style={{ marginLeft: "10px" }}>
                  <CalendarSaveButton
                    event={{
                      title: `Ora Planetara: ${hour.planet}`,
                      description: `Ora guvernata de planeta: ${hour.planet}\nSimbol: ${hour.symbol}`,
                      startTime: hour.startTime,
                      endTime: hour.endTime,
                    }}
                    showIcon={false}
                  >
                    <img
                      src="/images/apple-calendar-icon.png"
                      alt="Calendar"
                      width="16"
                      height="16"
                    />
                  </CalendarSaveButton>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlanetaryHoursList;
